/**
 * Deciding the registrations that are waiting — owned by this feature,
 * implemented in `infrastructure/` (§3.4, §4.1).
 *
 * Both decisions run through a Cloud Function. Approving creates a
 * `userAccounts` link, which no client may write at any role (ADR-004): the
 * link is the access predicate the whole system reads, so the only place it is
 * created is trusted code holding Admin credentials.
 */

/** A record the reviewer is considering linking a registration to. */
export interface EmployeeCandidate {
  readonly id: string;
  readonly employeeNumber: string;
  readonly fullName: string;
  readonly branchId: string | null;
  /** Already linked to somebody. One record, one account (FR-007). */
  readonly alreadyLinked: boolean;
}

export type ReviewFailureCode =
  | 'forbidden'
  | 'not_found'
  | 'already_linked'
  | 'record_taken'
  | 'network'
  | 'unknown';

export class ReviewError extends Error {
  readonly code: ReviewFailureCode;

  constructor(code: ReviewFailureCode, message: string) {
    super(message);
    this.name = 'ReviewError';
    this.code = code;
  }
}

export interface RegistrationReviewPort {
  /**
   * Finds the record a reviewer names by its employee number.
   *
   * By number rather than by name: the reviewer is holding the roster, and a
   * name search over 872 people would return several أحمد محمد and invite the
   * wrong one to be picked.
   */
  findByEmployeeNumber(employeeNumber: string): Promise<EmployeeCandidate | null>;

  /** Links the applicant to the record. The server records who approved. */
  approve(applicantUid: string, employeeId: string): Promise<void>;

  /** Refuses the request, with a reason the applicant will be shown. */
  reject(applicantUid: string, reasonAr: string): Promise<void>;
}
